import type { CitationDetail, CitationSite, Sources } from "@crux/contracts";
import type { paths } from "../api-types.js";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Sources a page at a time, one cited page opened up, and one cited site.
 */

/** What one Sources table asks the server for: the filter, the search, the order, the page. */
export type SourcesQuery = NonNullable<
  paths["/api/workspaces/{workspaceId}/reports/{reportId}/sources"]["get"]["parameters"]["query"]
>;

/**
 * Sources' summary cards and one page of one table, searched, narrowed and
 * sorted by the server from one read (DESIGN.md §9, "Sources").
 */
export async function fetchSources(
  workspaceId: string,
  reportId: string,
  query: SourcesQuery,
): Promise<Sources> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/sources",
    { params: { path: { workspaceId, reportId }, query } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "load these sources"));
  }
  return data;
}

/**
 * Every row of the table on screen as a file, made by the server: a link the
 * browser downloads, with the session cookie, rather than rows held here.
 */
export function sourcesCsvHref(
  workspaceId: string,
  reportId: string,
  query: Omit<SourcesQuery, "offset">,
): string {
  const search = new URLSearchParams(
    Object.entries(query).flatMap(([key, value]) =>
      typeof value === "string" ? [[key, value]] : [],
    ),
  );
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(reportId)}/sources.csv?${search.toString()}`;
}

export async function fetchCitationDetail(
  workspaceId: string,
  reportId: string,
  url: string,
  query: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
): Promise<CitationDetail> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/citations/page",
    { params: { path: { workspaceId, reportId }, query: { ...query, url } } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "open this page"));
  }
  return data;
}

/** One site, every page of it together, and the questions it was cited on. */
export async function fetchCitationSite(
  workspaceId: string,
  reportId: string,
  domain: string,
  query: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
): Promise<CitationSite> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/citations/site",
    { params: { path: { workspaceId, reportId }, query: { ...query, domain } } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "open this site"));
  }
  return data;
}
